/**
 * useExpiredVisitorAutoRevoke
 *
 * Takes the face off the reader when a visitor's inside time runs out, without
 * anyone having to open a screen and click.
 *
 * WHY THIS EXISTS: the backend expiry sweep (CleanupService.expireVisitors) marks
 * an overstaying visitor `expired` and routes removal into `device_sync_queue` —
 * the CLOUD queue. The cloud never contacts ControlID/Hikvision readers, and the
 * Electron DIRECT drainer (useDeferredDeviceRemovals) polls a different table,
 * `deferred_device_removals`, which is only filled on check-out and by the service-
 * provider scheduler. So an overstayed visitor kept working face access until a
 * porteiro noticed them in "Visitantes Expirados" and removed them by hand.
 *
 * HOW IT WORKS (device-truth first):
 *   1. Ask each reader who is actually enrolled (checkPresenceOnDevices).
 *   2. Remove only the expired visitors the device itself still reports, and only
 *      from the readers they were found on.
 *   3. Report every per-device outcome so device_credential_states reflects reality.
 *
 * One LOAD_PEOPLE per device per cycle costs far less than blind removal attempts
 * for every expired visitor on every device, forever — and expired visitors
 * accumulate, so blind retries would grow without bound.
 *
 * Electron only: in a browser this is a no-op, exactly like every other device path.
 * Mounted in OperationalView, alongside useDeferredDeviceRemovals.
 */

import { useCallback, useEffect, useRef } from 'react';
import { fetchExpiredVisitors } from './useExpiredVisitors';
import { expiredVisitorDeviceService } from '../services/expiredVisitorDeviceService';
import { reportCredentialOutcome } from '../utils/deviceRevoke';
import { isElectron } from '../utils/platform';
import { logger } from '../services/logger';

/** Expiry is not urgent — the porteiro's screen is still there for anything in a hurry. */
export const EXPIRED_REVOKE_INTERVAL_MS = 5 * 60_000;

/**
 * Initial delay so a freshly opened app isn't competing with login/first paint —
 * and so the expired-visitor list has actually loaded before the first sweep.
 */
export const INITIAL_DELAY_MS = 20_000;

/**
 * Fire this to drain immediately instead of waiting for the next tick — used by the
 * Visitantes Expirados screen after a manual action, and by tests. Mirrors
 * 'panther:deferred-removals-poll'.
 */
export const EXPIRED_REVOKE_POLL_EVENT = 'panther:expired-visitor-revoke-poll';

/**
 * How many visitors one cycle may clear. Each one costs a removal per reader plus a
 * credential-outcome POST, so an unbounded sweep over a long backlog of expired
 * visitors would burst the network (and the audit log — see the RDS storage
 * incident). The remainder is picked up on the next cycle.
 */
export const MAX_VISITORS_PER_CYCLE = 5;

export function useExpiredVisitorAutoRevoke(customerId: string | null | undefined) {
  const runningRef = useRef(false);

  const runCycle = useCallback(async () => {
    if (!customerId) return;
    // The cloud must never contact a reader; a browser leaves this to the on-site app.
    if (!isElectron()) return;
    if (runningRef.current) return;

    runningRef.current = true;
    try {
      // Fetched per cycle rather than held in render state: this runs on a timer,
      // and a stale list would either miss a just-expired visitor or keep retrying
      // someone already cleared.
      const expired = await fetchExpiredVisitors(customerId);
      if (expired.length === 0) return;

      const { visitorOnDevices } = await expiredVisitorDeviceService.checkPresenceOnDevices(
        customerId,
        expired,
      );
      if (visitorOnDevices.size === 0) return;

      const stillEnrolled = expired
        .filter(v => (visitorOnDevices.get(v.id) || []).length > 0)
        .slice(0, MAX_VISITORS_PER_CYCLE);

      logger.info('visitor.expired.autoRevoke', `Clearing ${stillEnrolled.length} expired visitor(s) still on devices`, {
        found: visitorOnDevices.size,
      });

      for (const v of stillEnrolled) {
        const deviceIds = visitorOnDevices.get(v.id)!;
        try {
          // Narrow to the readers that actually still hold this person.
          const results = await expiredVisitorDeviceService.removeVisitorFromDevices(
            customerId,
            { ...v, devices: deviceIds },
          );
          results.forEach(r => {
            reportCredentialOutcome(customerId, v.personId, r.deviceId, {
              success: r.success,
              latencyMs: r.latencyMs ?? 0,
              source: r.source ?? 'local',
              errorMessage: r.error,
            });
          });
          const failed = results.filter(r => !r.success).length;
          if (failed > 0) {
            // Left on the device on purpose: the next cycle's presence check finds
            // them again, so a failure retries instead of being lost.
            logger.warn('visitor.expired.autoRevoke', `"${v.name}" still on ${failed} reader(s) — will retry next cycle`);
          }
        } catch (err) {
          logger.error('visitor.expired.autoRevoke', `Removal threw for "${v.name}"`, err as Error);
        }
      }
    } catch (err) {
      // A presence check that fails tells us nothing about the readers — never treat
      // it as "everyone is clear", just try again next cycle.
      logger.error('visitor.expired.autoRevoke', 'Cycle failed', err as Error);
    } finally {
      runningRef.current = false;
    }
  }, [customerId]);

  useEffect(() => {
    if (!customerId) return;

    const initialTimer = setTimeout(runCycle, INITIAL_DELAY_MS);
    const intervalId = setInterval(runCycle, EXPIRED_REVOKE_INTERVAL_MS);
    const onPoll = () => { void runCycle(); };
    window.addEventListener(EXPIRED_REVOKE_POLL_EVENT, onPoll);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalId);
      window.removeEventListener(EXPIRED_REVOKE_POLL_EVENT, onPoll);
    };
  }, [customerId, runCycle]);
}
